import type { ReactElement } from "react";
import { useCallback, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { FormSelectCombobox } from "app/pages/sales-intelligence/common-components/dropdown/FormSelectCombobox";
import { StyledOperatorFieldRow } from "app/pages/sales-intelligence/common-components/dropdown/styles";
import { FloatContainer } from "app/pages/sales-intelligence/common-components/FloatContainer/FloatContainer";
import { FormFieldLabel, MultiSelectAutocomplete } from "app/pages/sales-intelligence/common-components/form-fields";
import { FormDropdownItemTag } from "app/pages/sales-intelligence/common-components/form-fields/components/FormCheckboxDropdownItem/FormDropdownItemTag";
import type { FormSelectValue } from "app/pages/sales-intelligence/common-components/form-fields/components/FormSelect/types";
import type { IFilterViewBaseModelProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";

import type { IAppsPermissionsFilter } from "../../../types/commonFiltersDto";
import type { IAppsPermissionItemDto, IAppsPermissionsDescriptionDto } from "../../../types/descriptionDto";

const OPERATOR_FILTER_WIDTH = 77;
const PERMISSIONS_FILTER_BUTTON_WIDTH = 178;
const PERMISSIONS_FILTER_WIDTH = 446;

const AppPermissionsFilter = ({
    filter,
    value,
    onFilterUpdate,
}: IFilterViewBaseModelProps<IAppsPermissionsFilter, IAppsPermissionsDescriptionDto>): ReactElement => {
    const { translate } = useTranslate();

    const { permissions, operatorOptions } = filter.options;

    const [operator, setOperator] = useState<string | undefined>(operatorOptions?.[0]);
    const [isOpenPermissionsSelect, setIsOpenPermissionsSelect] = useState(false);

    const handleOnToggleClick = (open: boolean) => {
        setIsOpenPermissionsSelect(open);
    };

    const handleOperatorChange = (newValue: string): void => {
        setOperator(newValue);

        if (!value?.permissions) {
            return;
        }

        onFilterUpdate({ ...value, operator: newValue });
    };

    const handlePermissionsChange = (newPermissionsValue: FormSelectValue[]): void => {
        if (!operator) {
            return;
        }

        if (!newPermissionsValue.length) {
            onFilterUpdate();
            return;
        }

        onFilterUpdate({
            permissions: newPermissionsValue.map((newValue) => String(newValue)),
            operator,
        });
    };

    const sensitiveTagText = translate("si.find_apps.filters.permissions.tagText");

    const renderOptionTag = useCallback(
        (option: IAppsPermissionItemDto): ReactElement | null =>
            option.isSensitive ? <FormDropdownItemTag>{sensitiveTagText}</FormDropdownItemTag> : null,
        [sensitiveTagText],
    );

    return (
        <FormFieldLabel text={translate("si.find_apps.filters.permissions.label")}>
            <StyledOperatorFieldRow>
                <FormSelectCombobox<string>
                    width={OPERATOR_FILTER_WIDTH}
                    fieldName="permissionsOperator"
                    value={operator}
                    options={operatorOptions}
                    formatValue={(option) => option.toLocaleUpperCase()}
                    onChange={handleOperatorChange}
                />

                <FloatContainer
                    isFloat={isOpenPermissionsSelect}
                    deps={value?.permissions}
                >
                    <MultiSelectAutocomplete<IAppsPermissionItemDto>
                        width={PERMISSIONS_FILTER_WIDTH}
                        buttonWidth={PERMISSIONS_FILTER_BUTTON_WIDTH}
                        isDisplayValue={false}
                        fieldName="permissions"
                        hasSearch
                        value={[...(value?.permissions || [])]}
                        options={permissions}
                        searchPlaceHolder={translate("common.placeholder.search")}
                        placeholder={translate("common.placeholder.search")}
                        onChange={handlePermissionsChange}
                        formatValue={(option) => option}
                        getOptionId={(option) => option.name}
                        getOptionTitle={(option) => option.name}
                        getOptionSubTitle={(option) => option.description}
                        renderOptionTag={renderOptionTag}
                        onToggle={handleOnToggleClick}
                    />
                </FloatContainer>
            </StyledOperatorFieldRow>
        </FormFieldLabel>
    );
};

export default AppPermissionsFilter;
